import { TableRow } from '@/api/models/TableRow';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useStore } from '@nanostores/react';
import { TableModulesService, TableRowsService } from '@/api';
import { TableCell } from './TableCell';
import { toast } from 'sonner';
import { CalendarDatePicker } from '@/components/calenderDatepicker';

interface EditableHeaderProps {
  column: string;
  isCoreColumn: boolean;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: (newValue: string) => void;
}

const EditableHeader = ({ 
  column, 
  isCoreColumn, 
  isEditing, 
  onStartEdit, 
  onEndEdit 
}: EditableHeaderProps) => {
  const [value, setValue] = useState(column);

  if (isEditing && !isCoreColumn) {
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => onEndEdit(value)}
        autoFocus
        className="w-full p-1 border rounded"
      />
    );
  }

  return (
    <div 
      onClick={!isCoreColumn ? onStartEdit : undefined}
      className={`p-2 ${!isCoreColumn ? 'cursor-pointer hover:bg-gray-100' : ''}`}
    >
      {column}
    </div>
  );
};

const STATUS_OPTIONS = [
  'Open',
  'In Progress',
  'On Hold',
  'Resolved',
  'Escalated',
  'Closed',
  'Reopened',
  'Pending Review',
  'Cancelled'
];

const PRIORITY_OPTIONS = [
  'Low',
  'Medium',
  'High',
  'Critical',
  'None'
];

const PROGRESS_OPTIONS = [
  'Not Started',
  'In Progress',
  'Nearly Complete',
  'Completed',
  'Stalled'
];

export const ComplaintListing = () => {
  const org = useStore($org);
  const queryClient = useQueryClient();

  // State management
  const [additionalColumns, setAdditionalColumns] = useState<string[]>([]);
  const [editingCell, setEditingCell] = useState<{rowIndex: number, column: string} | null>(null);
  const [localRows, setLocalRows] = useState<TableRow[]>([]);
  const [newRows, setNewRows] = useState<TableRow[]>([]);
  const [editingHeader, setEditingHeader] = useState<string | null>(null);

  // Data fetching
  const { 
    data: tableModule, 
    isLoading: isLoadingStructure 
  } = useQuery({
    queryKey: ['GetTableModules', org?.id],
    enabled: !!org?.id,
    queryFn: () => TableModulesService.getTableModules(
      org!.id,
      "Complaints Management Module"
    )
  });

  const { 
    isLoading: isLoadingData 
  } = useQuery({
    queryKey: ['GetTableRows', org?.id, tableModule?.id],
    enabled: !!org?.id && !!tableModule?.id,
    queryFn: async () => {
      const result = await TableRowsService.getTableRows(org!.id, tableModule!.id);
      const mappedResult = result.map(dto => ({
        id: dto.id || `temp-${Date.now()}`,
        name: dto.name || '',
        createdAtUtc: dto.createdAtUtc || new Date().toISOString(),
        coreData: dto.coreData,
        customData: dto.customData,
        comments: dto.comments || [],
        commentCount: dto.commentCount || 0,
        tableModuleId: tableModule!.id,
        organizationId: org!.id
      }));
      setLocalRows(mappedResult);
      return mappedResult;
    }
  });

  // Utility functions
  const getAllColumns = () => {
    if (!tableModule) return [];
    return [
      ...(tableModule.coreColumns ?? []),
      ...(tableModule.customColumns ?? []),
      ...additionalColumns
    ];
  };

  // Event handlers
  const handleAddRow = () => {
    if (!org || !tableModule) return;

    const newRow: TableRow = {
      id: `temp-${Date.now()}`,
      name: '',
      createdAtUtc: new Date().toISOString(),
      tableModuleId: tableModule.id,
      organizationId: org.id,
      coreData: {},
      customData: {},
      commentCount: 0,
      comments: []
    };
    setNewRows([...newRows, newRow]);
  };

  const handleAddColumn = () => {
    const newColumnName = `Column ${additionalColumns.length + 1}`;
    setAdditionalColumns([...additionalColumns, newColumnName]);
  };

  const handleSaveColumns = async () => {
    if (!org?.id || !tableModule?.id) return;

    try {
      await TableModulesService.updateTableModules(
        org.id,
        tableModule.id,
        {
          columnHeaders: getAllColumns()
        }
      );
      toast.success('Columns saved successfully!');
    } catch (error) {
      console.error('Error saving columns:', error);
      toast.error('Failed to save columns. Please try again.');
    }
  };

  const handleSaveRows = async () => {
    if (!org?.id || !tableModule?.id) return;

    const allRows = [...localRows, ...newRows];
    try {
      const response = await TableRowsService.updateTableRows(
        org.id,
        tableModule.id,
        {
          rows: allRows.map(row => JSON.stringify({
            id: row.id,
            CoreData: row.coreData,
            CustomData: row.customData
          }))
        }
      );

      // Clear new rows
      setNewRows([]);
      
      // Invalidate and refetch the data
      await queryClient.invalidateQueries({ 
        queryKey: ['GetTableRows', org.id, tableModule.id] 
      });

      toast.success('Rows saved successfully!');
    } catch (error) {
      console.error('Error saving rows:', error);
      toast.error('Failed to save rows. Please try again.');
    }
  };

  const handleCellEdit = (rowIndex: number, column: string, value: string) => {
    const isNewRow = rowIndex >= localRows.length;
    const targetRows = isNewRow ? newRows : localRows;
    const targetIndex = isNewRow ? rowIndex - localRows.length : rowIndex;

    const updatedRows = targetRows.map((row, index) => {
      if (index !== targetIndex) return row;

      const isCoreColumn = tableModule?.coreColumns.includes(column);
      return {
        ...row,
        [isCoreColumn ? 'coreData' : 'customData']: {
          ...row[isCoreColumn ? 'coreData' : 'customData'],
          [column]: value
        }
      };
    });

    if (isNewRow) {
      setNewRows(updatedRows);
    } else {
      setLocalRows(updatedRows);
    }
  };

  const handleHeaderEdit = (oldColumnName: string, newColumnName: string) => {
    if (oldColumnName === newColumnName) {
      setEditingHeader(null);
      return;
    }

    // Update additional columns
    setAdditionalColumns(prev => 
      prev.map((col: string) => col === oldColumnName ? newColumnName : col)
    );

    // Update custom columns if the column exists there
    if (tableModule?.customColumns.includes(oldColumnName)) {
      const updatedCustomColumns = tableModule.customColumns.map((col: string) => 
        col === oldColumnName ? newColumnName : col
      );
      
      // Update the tableModule state immediately
      const updatedTableModule = {
        ...tableModule,
        customColumns: updatedCustomColumns
      };

      // Update the table module in the backend
      TableModulesService.updateTableModules(
        org!.id,
        tableModule.id,
        {
          columnHeaders: [
            ...(updatedTableModule.coreColumns ?? []),
            ...updatedCustomColumns,
            ...additionalColumns
          ]
        }
      ).then(() => {
        // Ensure this is not inside a non-component function
        if (org?.id) {
          queryClient.invalidateQueries({ 
            queryKey: ['GetTableModules', org.id] 
          });
        }
      });
    }

    // Update the data in rows
    const updateRowData = (rows: TableRow[]) => 
      rows.map(row => ({
        ...row,
        customData: {
          ...row.customData,
          [newColumnName]: row.customData?.[oldColumnName],
        }
      }));

    setLocalRows(updateRowData(localRows));
    setNewRows(updateRowData(newRows));
    setEditingHeader(null);
  };

  const handleSaveTable = async () => {
    if (!org?.id || !tableModule?.id) return;

    try {
      // Save columns
      await TableModulesService.updateTableModules(
        org.id,
        tableModule.id,
        {
          columnHeaders: getAllColumns()
        }
      );

      // Save rows
      const allRows = [...localRows, ...newRows];
      await TableRowsService.updateTableRows(
        org.id,
        tableModule.id,
        {
          rows: allRows.map(row => JSON.stringify({
            id: row.id,
            CoreData: row.coreData,
            CustomData: row.customData
          }))
        }
      );

      // Clear new rows
      setNewRows([]);
      
      // Invalidate and refetch the data
      await queryClient.invalidateQueries({ 
        queryKey: ['GetTableRows', org.id, tableModule.id] 
      });

      toast.success('Table saved successfully!');
    } catch (error) {
      console.error('Error saving table:', error);
      toast.error('Failed to save table. Please try again.');
    }
  };

  // Loading and error states
  if (isLoadingStructure || isLoadingData) {
    return <div>Loading...</div>;
  }

  if (!org || !tableModule) {
    return <div>No data available</div>;
  }

  // Render
  return (
    <div className="p-4">
      <div className="overflow-x-auto mb-4">
        <table className="min-w-full border-collapse border border-gray-200">
          <thead>
            <tr>
              {getAllColumns().map((column, index) => {
                const isCoreColumn = tableModule.coreColumns.includes(column);
                return (
                  <th 
                    key={`${column}-${index}`} 
                    className="border border-gray-200 bg-gray-50"
                  >
                    <EditableHeader
                      column={column}
                      isCoreColumn={isCoreColumn}
                      isEditing={editingHeader === column}
                      onStartEdit={() => setEditingHeader(column)}
                      onEndEdit={(newValue) => handleHeaderEdit(column, newValue)}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {[...localRows, ...newRows].map((row, rowIndex) => (
              <tr key={row.id || `new-${rowIndex}`}>
                {getAllColumns().map((column, colIndex) => {
                  const isCoreColumn = tableModule.coreColumns.includes(column);
                  const value = isCoreColumn
                    ? row.coreData?.[column] || ''
                    : row.customData?.[column] || '';
                  const isEditing = editingCell?.rowIndex === rowIndex && 
                                  editingCell?.column === column;
                  
                  const isStatusColumn = column === 'Status';
                  const isPriorityColumn = column === 'Priority';
                  const isProgressColumn = column === 'Progress';
                  const isDueDateColumn = column === 'Due Date';

                  return (
                    <td key={`${column}-${colIndex}`} className="border border-gray-200">
                      {isStatusColumn ? (
                        <select
                          value={value}
                          onChange={(e) => handleCellEdit(rowIndex, column, e.target.value)}
                          className="w-full p-1 border rounded"
                        >
                          <option value="">Select Status</option>
                          {STATUS_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : isPriorityColumn ? (
                        <select
                          value={value}
                          onChange={(e) => handleCellEdit(rowIndex, column, e.target.value)}
                          className="w-full p-1 border rounded"
                        >
                          <option value="">Select Priority</option>
                          {PRIORITY_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : isProgressColumn ? (
                        <select
                          value={value}
                          onChange={(e) => handleCellEdit(rowIndex, column, e.target.value)}
                          className="w-full p-1 border rounded"
                        >
                          <option value="">Select Progress</option>
                          {PROGRESS_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : isDueDateColumn ? (
                        <div className="relative w-full">
                          <CalendarDatePicker
                            field={{
                              value: value ? new Date(value) : new Date(),
                              onChange: (date: Date | null) => handleCellEdit(rowIndex, column, date ? date.toISOString() : '')
                            }}
                            className="w-full"
                            calendarClassName="absolute left-0 z-50"
                          />
                        </div>
                      ) : (
                        <TableCell
                          value={value}
                          isEditing={isEditing}
                          onChange={(newValue) => handleCellEdit(rowIndex, column, newValue)}
                          onStartEdit={() => setEditingCell({ rowIndex, column })}
                          onEndEdit={() => setEditingCell(null)}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-start gap-2">
        <button
          onClick={handleAddColumn}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Add Column
        </button>
        <button
          onClick={handleAddRow}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Add Row
        </button>
        {/* <button
          onClick={handleSaveColumns}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Save Columns
        </button>
        <button
          onClick={handleSaveRows}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Save Rows
        </button> */}
        <button
          onClick={handleSaveTable}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Save Table
        </button>
      </div>
    </div>
  );
};
